import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {SERVER} from "../../../Constants"

const CACHE_EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds


const apiClient = axios.create({
    baseURL: SERVER, // Adjust the base URL if necessary
    withCredentials: true
});

const downloadSasFile = async (url) => {
    const query = { img: url };
    const response = await apiClient.get("user/getAccess", { params: query });
    return response.data;
};

const readCache = (url) => {
    try {
        const cachedItem = localStorage.getItem(url);
        if (!cachedItem) return null;
        const { data, lastUsed } = JSON.parse(cachedItem);
        if (Date.now() - lastUsed < CACHE_EXPIRATION_TIME) return data;
        localStorage.removeItem(url);
    } catch (error) {
        localStorage.removeItem(url);
    }
    return null;
};

/** Initial on a warm surface, shown while loading or when the image can't be fetched. */
const Placeholder = ({ alt, style }) => (
    <div
        role="img"
        aria-label={alt || 'No photo'}
        style={{
            ...style,
            backgroundColor: '#2e281f',
            color: '#ead7a7',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: 22,
        }}
    >
        <span aria-hidden="true">{alt ? alt[0].toUpperCase() : ''}</span>
    </div>
);

const ImageLoader = ({ source, alt, style }) => {
    const url = source?.uri;
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(Boolean(url));
    const [failed, setFailed] = useState(!url);
    // A cached access URL can expire before the cache does; allow one fresh download when it fails to load.
    const retried = useRef(false);

    const download = async () => {
        try {
            const data = await downloadSasFile(url);
            setFile(data);
            localStorage.setItem(url, JSON.stringify({ data, lastUsed: Date.now() }));
        } catch (error) {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        retried.current = false;
        setFailed(!url);
        if (!url) {
            setLoading(false);
            return;
        }
        const cached = readCache(url);
        if (cached) {
            setFile(cached);
            setLoading(false);
        } else {
            setLoading(true);
            download();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url]);

    const handleImageError = () => {
        if (retried.current) {
            setFailed(true);
            return;
        }
        retried.current = true;
        localStorage.removeItem(url);
        setLoading(true);
        download();
    };

    if (loading || failed || !file) return <Placeholder alt={alt} style={style} />;
    return <img src={file} alt={alt} style={style} onError={handleImageError} />;
};

export default ImageLoader;
